import { memo, useMemo, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { PublicView, PlayerPublic } from '@ringmaster/shared';
import { ROLE_META } from '@ringmaster/shared';
import { BroadcastFrame, SAFE, DESIGN_WIDTH, DESIGN_HEIGHT } from './BroadcastFrame.js';
import { ProsceniumFrame } from './ProsceniumFrame.js';
import { SceneStack } from '../fx/SceneStack.js';
import { scenesForGame } from '../assets/scenes/index.js';
import { useSequence } from '../fx/sequencer.js';
import { PlayerBust, Plaque, StageRevealShell, type StageRevealHandle } from './components/index.js';
import { ParticleLayer } from '../fx/index.js';
import { Glyph } from '../ui/Glyph.js';
import { ceremony, ornaments } from './ornaments.js';

/**
 * Stage DawnScreen — money-moment #2: the morning-after reveal of the night's kill
 * (DAWN phase). DAWN_REVEAL choreography:
 *   hush      — the survivors stand in a line under the dawn bleed
 *   spotlight — the iris snaps onto the slain player's bust
 *   name      — a CRACKED-PLAQUE name plate drops in with a rope-drop overshoot
 *   roleFlip  — claw-gouge slashes rake across (×3 staggered, 6px shake +
 *               bloodMotes burst), then the role is named
 * DAWN_SAFE (nobody died): an unbroken plaque + a dawnBird glyph + goldDust — the
 * relieved sunrise.
 *
 * The reveal FX (shake / spotlight / burst) run through StageRevealShell so the
 * coordinate math + plumbing live in one place. DawnScreenContent is the inner
 * screen (StageView frames it); DawnScreenDemo self-frames for CardLab.
 */

export interface DawnScreenProps {
  view: PublicView;
  isHost: boolean;
}

const BUST_SIZE = 150;
const ROW_Y = 700; // survivors line, design px
const ROW_SPREAD = 1180;
const PLAQUE_TOP = DESIGN_HEIGHT * 0.13; // where the plate drops in, design px
const PLAQUE_BAND = 300; // reserved vertical band for the (content-hugging) plate

function rowPositions(n: number): Array<{ x: number; y: number }> {
  if (n <= 0) return [];
  const step = n === 1 ? 0 : ROW_SPREAD / (n - 1);
  const startX = DESIGN_WIDTH / 2 - ROW_SPREAD / 2;
  return Array.from({ length: n }, (_, i) => ({ x: n === 1 ? DESIGN_WIDTH / 2 : startX + i * step, y: ROW_Y }));
}

function DawnScreenContentImpl({ view }: DawnScreenProps) {
  const reduced = useReducedMotion();
  const shellRef = useRef<StageRevealHandle>(null);
  const bustEls = useRef<Map<string, HTMLDivElement>>(new Map());

  const victimId = view.dawn?.victimId ?? null;
  const victim: PlayerPublic | null = victimId
    ? (view.players.find((p) => p.id === victimId) ?? null)
    : null;
  const victimIsWolf = victim?.revealedRole === 'WEREWOLF';
  const hasVictim = !!victim;

  const lineup = useMemo(
    () => view.players.filter((p) => p.alive || p.id === victimId),
    [view.players, victimId],
  );
  const positions = useMemo(() => rowPositions(lineup.length), [lineup.length]);

  const onReveal = useMemo(
    () => ({
      // spotlight-after-layout: driven from the cue, so busts have laid out.
      spotlight: () => shellRef.current?.spotlightOn(bustEls.current.get(victimId ?? ''), { r: 200 }),
      // The claw rake lands on roleFlip — a sharp 6px shake + a blood-mote burst.
      roleFlip: () => {
        if (reduced) return;
        shellRef.current?.shake(0.5);
        shellRef.current?.burstAt(bustEls.current.get(victimId ?? ''), { preset: 'bloodMotes', count: 40 });
      },
      shake: () => {
        if (reduced) return;
        shellRef.current?.shake(0.35);
      },
      burst: () => {
        if (reduced) return;
        shellRef.current?.burstAt(bustEls.current.get(victimId ?? ''), { preset: 'goldDust', count: 40 });
      },
    }),
    [reduced, victimId],
  );

  const reveal = useSequence('DAWN_REVEAL', { enabled: hasVictim, onCue: onReveal });
  const safe = useSequence('DAWN_SAFE', { enabled: !hasVictim });

  const showName = reveal.has('name');
  const showRole = reveal.has('roleFlip');
  const dimOthers = reveal.has('spotlight');

  return (
    <StageRevealShell ref={shellRef} spotlightDark={hasVictim ? 0.66 : 0}>
      {/* Ambient dawn fog. */}
      <ParticleLayer preset="fog" intensity={0.4} slot={2} />

      {/* Eyebrow */}
      <div
        style={{
          position: 'absolute',
          top: SAFE + 24,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: 'var(--font-ui, Inter, sans-serif)',
          fontSize: 'var(--stage-text-meta, 24px)',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--dawn-4, #e8975c)',
          zIndex: 2,
        }}
      >
        Dawn breaks over Ravenhollow
      </div>

      {hasVictim ? (
        <>
          {/* Survivors + victim line. */}
          {lineup.map((p, i) => {
            const pos = positions[i] ?? { x: DESIGN_WIDTH / 2, y: ROW_Y };
            const isVictim = p.id === victimId;
            const state = isVictim ? 'spotlit' : dimOthers ? 'dim' : 'normal';
            return (
              <div
                key={p.id}
                ref={(el) => {
                  if (el) bustEls.current.set(p.id, el);
                  else bustEls.current.delete(p.id);
                }}
                style={{
                  position: 'absolute',
                  left: pos.x - BUST_SIZE / 2,
                  top: pos.y - BUST_SIZE / 2,
                  width: BUST_SIZE,
                  zIndex: isVictim ? 3 : 2,
                }}
              >
                <PlayerBust
                  player={p}
                  size={BUST_SIZE}
                  state={state === 'spotlit' && !showName ? 'normal' : state}
                />
              </div>
            );
          })}

          {/* Cracked-plaque name plate — drops in with a rope-drop overshoot. */}
          {showName && victim && (
            <PlaqueCard
              name={victim.name}
              eyebrow="Slain in the night"
              cracked
              slashed={showRole}
              nameSize="var(--stage-text-title, 96px)"
              reduced={!!reduced}
            />
          )}

          {/* Role line, below the plaque. */}
          {showRole && victim?.revealedRole && (
            <div
              style={{
                position: 'absolute',
                top: PLAQUE_TOP + PLAQUE_BAND + 12,
                left: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 14,
                zIndex: 5,
              }}
            >
              <Glyph
                name={victimIsWolf ? 'wolfHead' : victim.revealedRole === 'SEER' ? 'seerEye' : 'candle'}
                tone={victimIsWolf ? 'blood' : 'gold'}
                size={44}
              />
              <span
                style={{
                  fontFamily: 'var(--font-display, serif)',
                  fontSize: 'var(--stage-text-caption, 44px)',
                  color: victimIsWolf ? 'var(--blood-6, #ff5a4f)' : 'var(--gold-6, #f7e3b0)',
                  letterSpacing: '0.04em',
                }}
              >
                They were a {ROLE_META[victim.revealedRole].label}
              </span>
            </div>
          )}
        </>
      ) : (
        // DAWN_SAFE — no one died: unbroken plaque + dawnBird + goldDust.
        <>
          <ParticleLayer preset="goldDust" intensity={0.5} slot={4} />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 28,
              zIndex: 3,
              opacity: safe.has('relief') || safe.has('hush') ? 1 : 0,
              transition: reduced ? 'none' : 'opacity 800ms var(--ease-standard)',
            }}
          >
            <Glyph name="dawnBird" tone="gold" size={140} title="The village wakes unharmed" />
            <PlaqueCard
              name="No one fell in the night"
              eyebrow="The village wakes"
              // A plaque, not a banner: cap the plate at ~60% of the 1920 design
              // width and let the sentence wrap to two lines.
              maxInlineSize={1150}
              nameSize="var(--stage-text-title, 96px)"
              wrap
              reduced={!!reduced}
            />
          </div>
        </>
      )}
    </StageRevealShell>
  );
}

/**
 * A carved plaque bearing a name — the DawnScreen's equivalent of NameCard, on the
 * CSS-only `Plaque` plate. It hugs the name so a short name like "EVE" reads as a
 * plate, not a stranded square. `cracked` uses the blood-tinted plate + crimson
 * eyebrow; `slashed` rakes faint claw-gouge strokes BEHIND the text (the plate's
 * overlay slot) so the eyebrow + name stay legible above them. `wrap` + a capped
 * `maxInlineSize` turn a long line ("No one fell in the night") into a two-line
 * plaque instead of a frame-spanning banner. The plate DROPS in with a rope-drop
 * overshoot (y past target then settles).
 */
function PlaqueCard({
  name,
  eyebrow,
  cracked = false,
  slashed = false,
  maxInlineSize,
  nameSize = 'var(--stage-text-hero, 144px)',
  wrap = false,
  reduced,
}: {
  name: string;
  eyebrow?: string;
  cracked?: boolean;
  slashed?: boolean;
  maxInlineSize?: number | string;
  nameSize?: string;
  wrap?: boolean;
  reduced: boolean;
}) {
  const clawSlashes = slashed ? (
    <>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          aria-hidden
          initial={reduced ? { opacity: 0 } : { opacity: 0, scaleX: 0 }}
          animate={{ opacity: 0.5, scaleX: 1 }}
          transition={{ delay: reduced ? 0 : 0.08 * i, duration: reduced ? 0 : 0.14, ease: 'easeOut' }}
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${ceremony.clawGouge})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            transform: `translateX(${(i - 1) * 40}px) rotate(${(i - 1) * 6}deg)`,
            transformOrigin: 'center',
            // The gouge art is black-on-transparent; over the flat dark plate it
            // reads as a faint shadowed rake at low opacity — text legibility wins,
            // so it stays subtle and never occludes the eyebrow/name.
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      ))}
    </>
  ) : null;

  return (
    <motion.div
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: -220 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        reduced
          ? { duration: 0 }
          : { type: 'spring', stiffness: 260, damping: 16, mass: 1.1 } // rope-drop overshoot
      }
      style={{
        position: 'absolute',
        top: PLAQUE_TOP,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        zIndex: 4,
      }}
    >
      <Plaque
        variant={cracked ? 'cracked' : 'clean'}
        overlay={clawSlashes}
        minInlineSize={480}
        maxInlineSize={maxInlineSize}
        padBlock={40}
        padInline={72}
        radius={22}
      >
        {eyebrow && (
          <span
            style={{
              fontFamily: 'var(--font-ui, Inter, sans-serif)',
              fontSize: 'var(--stage-text-meta, 24px)',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: cracked ? 'var(--blood-6, #ff5a4f)' : 'var(--gold-5, #e6b866)',
              textShadow: '0 1px 6px rgba(0,0,0,0.9)',
            }}
          >
            {eyebrow}
          </span>
        )}
        <span
          style={{
            fontFamily: 'var(--font-display, serif)',
            fontSize: nameSize,
            lineHeight: wrap ? 1.1 : 1,
            textAlign: 'center',
            whiteSpace: wrap ? 'normal' : 'nowrap',
            background: 'linear-gradient(180deg, var(--gold-6,#f7e3b0), var(--gold-3,#a9762f))',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
            padding: '0 8px',
            filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.85))',
          }}
        >
          {name}
        </span>
      </Plaque>
    </motion.div>
  );
}

export const DawnScreenContent = memo(DawnScreenContentImpl);

/** CardLab / standalone — mounts the full frame stack (DAWN scene). */
export function DawnScreenDemo({ view, isHost }: DawnScreenProps) {
  const manifest = scenesForGame('bloodmoon');
  const inner = <DawnScreenContent view={view} isHost={isHost} />;
  return (
    <BroadcastFrame>
      {manifest ? (
        <SceneStack manifest={manifest} phase={view.phase}>
          <ProsceniumFrame cornerSrc={ornaments.filigreeCorner} grainSrc={ornaments.grainTile}>{inner}</ProsceniumFrame>
        </SceneStack>
      ) : (
        <ProsceniumFrame cornerSrc={ornaments.filigreeCorner} grainSrc={ornaments.grainTile}>{inner}</ProsceniumFrame>
      )}
    </BroadcastFrame>
  );
}
